import { HOST_DOMAIN } from "../utils/domain";
import { refreshAccessToken } from "../api/rest/user";
import {
  BrandclubStoreConfigUtils,
  tryGetVisitorId,
} from "@brandclub/common-ui";
import { redeemBrandAppLoginCode } from "@/api/rest/appconfig";

const sharedAuthCookieName =
  BrandclubStoreConfigUtils.getAppStage() === "prod"
    ? "bcSessionToken"
    : `bcSessionToken-${BrandclubStoreConfigUtils.getAppStage()}`;

const sharedRefreshTokenCookieName =
  BrandclubStoreConfigUtils.getAppStage() === "prod"
    ? "bcRefreshToken"
    : `bcRefreshToken-${BrandclubStoreConfigUtils.getAppStage()}`;

const sharedVisitorIdCookieName =
  BrandclubStoreConfigUtils.getAppStage() === "prod"
    ? "bcVisitorId"
    : `bcVisitorId-${BrandclubStoreConfigUtils.getAppStage()}`;

const getTopLevelDomain = () => {
  const url = new URL(window.location.href);
  if (!HOST_DOMAIN.includes(url.hostname)) {
    return url.hostname;
  } else {
    return HOST_DOMAIN.split(".").slice(-2).join(".");
  }
};

export const clearSharedAuthCookie = () => {
  // get top level domain
  const topLevelDomain = getTopLevelDomain();
  document.cookie = `${sharedAuthCookieName}=; path=/; domain=.${topLevelDomain}; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=None; Secure`;
};

export const clearSharedRefreshTokenCookie = () => {
  // get top level domain
  const topLevelDomain = getTopLevelDomain();
  document.cookie = `${sharedRefreshTokenCookieName}=; path=/; domain=.${topLevelDomain}; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=None; Secure`;
};

const isValidJWTToken = async (token: string) => {
  const tokenPayload = token.split(".");
  if (tokenPayload.length === 3) {
    const payload = JSON.parse(atob(tokenPayload[1]));
    return !payload.tokenType || payload.tokenType !== "dtcBrandAuth";
  }
  return false;
};

export const setSharedAuthCookie = async (jwt: string, expires: number) => {
  // get top level domain
  const topLevelDomain = getTopLevelDomain();

  if (jwt && jwt !== "") {
    const date = new Date(expires * 1000);
    document.cookie = `${sharedAuthCookieName}=${jwt}; path=/; domain=.${topLevelDomain}; expires=${date.toUTCString()}; SameSite=None; Secure`;
  }
};

export const setSharedRefreshTokenCookie = async (
  jwt: string,
  expires: number
) => {
  // get top level domain
  const topLevelDomain = getTopLevelDomain();

  if (jwt && jwt !== "") {
    const date = new Date(expires * 1000);
    document.cookie = `${sharedRefreshTokenCookieName}=${jwt}; path=/; domain=.${topLevelDomain}; expires=${date.toUTCString()}; SameSite=None; Secure`;
  }
};

export const getSharedAuthCookie = async () => {
  const url = new URL(window.location.href);
  const loginCode = url.searchParams.get("bcLoginCode");
  if (loginCode) {
    const res = await redeemBrandAppLoginCode({ loginCode });
    if (
      res.status === "success" &&
      res.data?.accessToken &&
      res.data?.refreshToken
    ) {
      await setSharedAuthCookie(
        res.data.accessToken,
        res.data.accessTokenExpiresIn
      );
      await setSharedRefreshTokenCookie(
        res.data.refreshToken,
        res.data.refreshTokenExpiresIn
      );
      url.searchParams.delete("bcLoginCode");
      window.location.href = url.toString();
      return res.accessToken;
    }
  }

  const name = `${sharedAuthCookieName}=`;
  const decodedCookie = decodeURIComponent(document.cookie);
  const ca = decodedCookie.split(";");
  let token = "";
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === " ") {
      c = c.substring(1);
    }
    if (c.indexOf(name) === 0) {
      token = c.substring(name.length, c.length);
    }
  }

  if (await isValidJWTToken(token)) {
    return token;
  } else {
    // try to refresh token
    const refreshTokenFromCookie = getRefreshTokenCookie();

    if (refreshTokenFromCookie) {
      try {
        const [clientId, refreshToken] = refreshTokenFromCookie.split("|");
        const newToken = await refreshAccessToken({ refreshToken, clientId });
        if (newToken?.accessToken) {
          await setSharedAuthCookie(newToken.accessToken, newToken.expires);
          return newToken.accessToken;
        }
      } catch (e) {
        console.error("Error refreshing access token", e);
        clearSharedRefreshTokenCookie();
      }
    }
    clearSharedAuthCookie();

    return "";
  }
};

export const getVisitorIdCookie = () => {
  const name = `${sharedVisitorIdCookieName}=`;
  const decodedCookie = decodeURIComponent(document.cookie);
  const ca = decodedCookie.split(";");
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === " ") {
      c = c.substring(1);
    }
    if (c.indexOf(name) === 0) {
      return c.substring(name.length, c.length);
    }
  }
  return "";
};

export const setSharedVisitorIdCookie = (
  visitorId: string,
  expirationDays: number
) => {
  const visitorIdCookie = getVisitorIdCookie();
  if (visitorIdCookie && visitorIdCookie === visitorId) {
    return;
  }
  const d = new Date();
  d.setTime(d.getTime() + expirationDays * 24 * 60 * 60 * 1000);
  const expires = `${d.toUTCString()}`;
  // get top level domain
  const topLevelDomain = getTopLevelDomain();
  document.cookie = `${sharedVisitorIdCookieName}=${visitorId}; path=/; domain=.${topLevelDomain}; expires=${expires}; SameSite=None; Secure`;
};

export const getAuthorizationHeader = async () => {
  const headers: any = {};
  const sessionToken = await getSharedAuthCookie();
  if (sessionToken && sessionToken !== "") {
    headers["Authorization"] = `Bearer ${sessionToken}`;
  }
  return headers;
};

export const getRefreshTokenCookie = () => {
  const name = `${sharedRefreshTokenCookieName}=`;
  const decodedCookie = decodeURIComponent(document.cookie);
  const ca = decodedCookie.split(";");
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === " ") {
      c = c.substring(1);
    }
    if (c.indexOf(name) === 0) {
      return c.substring(name.length, c.length);
    }
  }
  return "";
};

export const getHeadersWithUniqueVisitorId = async () => {
  const uniqueVisitorId = await tryGetVisitorId();
  const uniqueVisitorIdFromCookie = getVisitorIdCookie();

  const uniqueVisitorIdToUse =
    uniqueVisitorIdFromCookie && uniqueVisitorIdFromCookie !== ""
      ? uniqueVisitorIdFromCookie
      : uniqueVisitorId;

  const headers: any = {};

  if (uniqueVisitorIdToUse) {
    headers["x-bc-visitor-id"] = uniqueVisitorIdToUse;
  }
  if (uniqueVisitorIdToUse) {
    return {
      "Content-type": "application/json",
      ...(await getAuthorizationHeader()),
      "x-bc-visitor-id": uniqueVisitorIdToUse,
    };
  }
  return {
    "Content-type": "application/json",
    ...(await getAuthorizationHeader()),
  };
};
